/**
 * Abstract base class for skills with a linear phase state machine.
 * Subclasses declare their phase sequence and per-phase durations.
 * The base handles all timer management, phase transitions, and completion tracking.
 */
import type { SkillEffect } from './SkillEffect';

/** Lifecycle phases a phased skill can be in. */
export type SkillPhase = 'idle' | 'windup' | 'active' | 'recovery' | 'cooldown';

/** Base class for skills with a declarative phase sequence (windup → active → recovery → cooldown). */
export abstract class PhasedSkillEffect implements SkillEffect {
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  abstract readonly skillId: string;

  // --- Phase state (managed by base) ---
  private _phase: SkillPhase = 'idle';
  private _phaseDurationMs: number = 0;
  private _phaseTimer: number = 0;
  private _complete: boolean = false;

  /** Whether this skill's current activation has already applied its hit. Reset on each activate(). */
  private _hitApplied: boolean = false;

  // --- Abstract: subclass declares sequence and durations ---

  /** Ordered phase sequence, e.g. ['windup','active','recovery','cooldown']. */
  protected abstract readonly phaseSequence: readonly SkillPhase[];

  /** Return the duration in ms for the given phase from this skill's config. */
  protected abstract getPhaseDuration(phase: SkillPhase): number;

  // --- Optional hooks (override as needed) ---

  /** Extra activation prerequisite beyond idle check. Default: true. */
  protected canActivateExtra(): boolean { return true; }

  /** Called after idle+extra checks pass, before entering the first phase. */
  protected onActivate(): void { /* no-op */ }

  /** Called when entering a new phase. Use for side effects (fire projectile, start tick timer). */
  protected onPhaseEnter(_phase: SkillPhase): void { /* no-op */ }

  /** Per-frame logic before the phase timer decrements. Area uses this for tick damage. */
  protected onUpdate(_delta: number): void { /* no-op */ }

  // --- Phase state getters ---

  get phase(): SkillPhase { return this._phase; }
  get phaseDurationMs(): number { return this._phaseDurationMs; }
  get phaseTimeRemainingMs(): number { return this._phaseTimer; }
  get cooldown(): number { return this._phase === 'cooldown' ? this._phaseTimer : 0; }
  get cooldownDuration(): number { return this.getPhaseDuration('cooldown'); }
  get hitApplied(): boolean { return this._hitApplied; }
  markHitApplied(): void { this._hitApplied = true; }

  /** Default: not interruptible during active phase. Override for custom rules. */
  get interruptible(): boolean {
    return this._phase !== 'active';
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'idle' && this.canActivateExtra();
  }

  getBlockReason(): string | null {
    if (this._phase !== 'idle') return 'cooldown';
    if (!this.canActivateExtra()) return 'not ready';
    return null;
  }

  activate(): boolean {
    if (this._phase !== 'idle') return false;
    if (!this.canActivateExtra()) return false;
    this._complete = false;
    this._hitApplied = false;
    this.onActivate();
    this.enterPhase(this.phaseSequence[0]);
    return true;
  }

  deactivate(): void { this.interrupt(); }

  isActive(): boolean {
    return this._phase !== 'idle' && this._phase !== 'cooldown';
  }

  isComplete(): boolean { return this._complete; }

  interrupt(): void {
    if (this._phase === 'idle' || this._phase === 'cooldown') return;
    this._phase = 'idle';
    this._phaseDurationMs = 0;
    this._phaseTimer = 0;
    this._complete = true;
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;
    this.onUpdate(delta);
    this._phaseTimer -= delta;
    if (this._phaseTimer <= 0) {
      this.advancePhase();
    }
  }

  destroy(): void {
    this._phase = 'idle';
    this._phaseTimer = 0;
  }

  // --- Internal ---

  private enterPhase(phase: SkillPhase): void {
    this._phase = phase;
    this._phaseDurationMs = phase === 'idle' ? 0 : this.getPhaseDuration(phase);
    this._phaseTimer = this._phaseDurationMs;
    this.onPhaseEnter(phase);
  }

  private advancePhase(): void {
    const idx = this.phaseSequence.indexOf(this._phase);
    const nextIdx = idx + 1;

    // Mark complete when leaving the penultimate phase
    if (nextIdx >= this.phaseSequence.length - 1) {
      this._complete = true;
    }

    // Past end of sequence → idle
    if (nextIdx >= this.phaseSequence.length) {
      this._phase = 'idle';
      this._phaseDurationMs = 0;
      this._phaseTimer = 0;
      return;
    }

    const next = this.phaseSequence[nextIdx];

    // Skip zero-duration cooldown (preserves existing behavior)
    if (next === 'cooldown' && this.getPhaseDuration('cooldown') <= 0) {
      this._phase = 'idle';
      this._phaseDurationMs = 0;
      this._phaseTimer = 0;
      return;
    }

    this.enterPhase(next);
  }
}
